import { useEffect, useState } from "react";
import clsx from 'clsx';
import Swal from 'sweetalert2';
import { PageTitle, useLayout } from "../../../_metronic/layout/core";
import { DefaultTitle } from "../../../_metronic/layout/components/header/page-title/DefaultTitle";
import { useAuth } from "../../contexts/AuthProvider";
import { IApiKey, IApiKeyCriada } from "../../interfaces/IApiKey";
import { IClientes } from "../../interfaces/IClientes";
import ApiKeyService from "../../services/ApiKeyService";
import ClienteService from "../../services/ClientesService";
import ApiKeyModalChave from "./ApiKeyModalChave";
import ApiKeyModalCriar from "./ApiKeyModalCriar";
import ApiKeyWebhook from "./ApiKeyWebhook";
import ApiKeyLogs from "./ApiKeyLogs";
import { avisoExpiracao, formatarData, formatarDataHora } from "./apiKeyUtils";

/*Tradução */
import '../../../i18n/config';
import { useTranslation } from 'react-i18next';

/**
 * DEVN3-627: gestão das API Keys da API pública (US DEVN3-623). Administrador vê as chaves do
 * próprio cliente; Master escolhe o cliente. A chave completa só existe no estado desta tela
 * entre a criação e o fechamento do modal — nunca vai para localStorage nem para a URL.
 * DEVN3-631: aba Webhook (um por cliente), com o mesmo seletor de cliente do Master.
 * DEVN3-636: aba Logs (operações com erros por item e chamadas HTTP).
 */
function ApiKeys() {
    const { usuario } = useAuth();
    const { classes } = useLayout();
    const { t } = useTranslation();
    const ehMaster = usuario?.perfil === 'Master';

    const [chaves, setChaves] = useState<IApiKey[]>([]);
    const [carregando, setCarregando] = useState<boolean>(true);
    const [clientes, setClientes] = useState<IClientes[]>([]);
    const [clienteId, setClienteId] = useState<number | undefined>(usuario?.clienteID);
    const [mostrarCriar, setMostrarCriar] = useState<boolean>(false);
    const [chaveCriada, setChaveCriada] = useState<IApiKeyCriada | null>(null);
    const [aba, setAba] = useState<'chaves' | 'webhook' | 'logs'>('chaves');

    useEffect(() => {
        if (ehMaster) {
            ClienteService.obterTodosGeral().then(setClientes);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        carregar();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [clienteId]);

    async function carregar() {
        setCarregando(true);
        setChaves(await ApiKeyService.listar(ehMaster ? clienteId : undefined));
        setCarregando(false);
    }

    async function revogar(chave: IApiKey) {
        const swalWithBootstrapButtons = Swal.mixin({
            customClass: {
                confirmButton: 'btn btn-modal-excluir ms-5',
                cancelButton: 'btn btn-modal-cancelar'
            },
            buttonsStyling: false
        });
        const result = await swalWithBootstrapButtons.fire({
            title: t('Revogar API Key'),
            text: t(`A chave "${chave.nome}" (${chave.prefixo}) deixará de funcionar e as integrações que a usam vão parar. Esta ação não pode ser desfeita.`),
            icon: 'warning',
            showCancelButton: true,
            cancelButtonText: t('Cancelar'),
            confirmButtonText: t('Revogar'),
            reverseButtons: true
        });

        if (result.isConfirmed && await ApiKeyService.revogar(chave.id)) {
            Swal.fire({ text: t('API Key revogada com sucesso'), timer: 3000, icon: 'success', showConfirmButton: false });
            await carregar();
        }
    }

    function Situacao({ chave }: { chave: IApiKey }) {
        if (chave.situacao === 'revogada') {
            return <span className="badge badge-light-danger">{t('Revogada')}</span>;
        }
        if (chave.situacao === 'expirada') {
            return <span className="badge badge-light-dark">{t('Expirada')}</span>;
        }

        const aviso = avisoExpiracao(chave.expiraEm);
        return <>
            <span className="badge badge-light-success">{t('Ativa')}</span>
            {aviso && <span className="badge badge-light-warning ms-2">{t(aviso)}</span>}
        </>;
    }

    return <>
        <PageTitle breadcrumbs={[]}>API Key</PageTitle>
        <div className='toolbar' id='kt_toolbar'>
            <div id='kt_toolbar_container' className={clsx(classes.toolbarContainer.join(' '), 'd-flex flex-stack')}>
                <DefaultTitle />
                {aba === 'chaves' &&
                    <div className='d-flex align-items-center py-1'>
                        <button onClick={() => setMostrarCriar(true)} className='btn btn-sm btn-primario'>
                            <i className="icon-lg text-white fas fa-plus"></i>
                            <span style={{ verticalAlign: 'middle' }}> {t('API KEY')} </span>
                        </button>
                    </div>}
            </div>
        </div>

        <ApiKeyModalCriar show={mostrarCriar} clienteId={ehMaster ? clienteId : undefined}
            onFechar={() => setMostrarCriar(false)}
            onCriada={(criada) => {
                setMostrarCriar(false);
                setChaveCriada(criada);
                carregar();
            }} />
        <ApiKeyModalChave chave={chaveCriada?.chave ?? null} nome={chaveCriada?.nome} onFechar={() => setChaveCriada(null)} />

        {ehMaster &&
            <div className="row mb-5">
                <div className="col-md-6 form-group">
                    <label htmlFor="api-key-cliente" className="form-label">{t('Cliente')}</label>
                    <select id="api-key-cliente" className="form-select" value={clienteId ?? ''}
                        onChange={(e) => setClienteId(Number(e.target.value))}>
                        {clientes.map((cliente) => (
                            <option key={cliente.id} value={cliente.id}>
                                {cliente.nomeFantasia || cliente.razaoSocial || `Cliente ${cliente.id}`}
                            </option>
                        ))}
                    </select>
                </div>
            </div>}

        <ul className="nav nav-tabs nav-line-tabs nav-line-tabs-2x mb-5 fs-5 fw-bold" role="tablist">
            {([['chaves', 'Chaves'], ['webhook', 'Webhook'], ['logs', 'Logs']] as const).map(([chave, rotulo]) => (
                <li className="nav-item" key={chave} role="presentation">
                    <button type="button" role="tab" aria-selected={aba === chave}
                        className={`nav-link text-active-primary bg-transparent px-0 me-6 ${aba === chave ? 'active' : ''}`}
                        style={{ borderTop: 0, borderLeft: 0, borderRight: 0 }}
                        onClick={() => setAba(chave)}>
                        {t(rotulo)}
                    </button>
                </li>
            ))}
        </ul>

        {aba === 'logs' ? <ApiKeyLogs clienteId={ehMaster ? clienteId : undefined} /> : aba === 'webhook' ? <ApiKeyWebhook clienteId={ehMaster ? clienteId : undefined} /> : <>
        <p className="text-muted">
            {t('Chaves de acesso para integrar outros sistemas à plataforma pela API pública. A chave completa é exibida uma única vez, na criação.')}
        </p>

        {carregando ? <p>{t('Carregando...')}</p>
            : chaves.length === 0 ? <h2 className="mt-5">{t('Nenhuma API Key cadastrada')}</h2>
                : <div className="table-responsive">
                    <table className="table table-row-dashed align-middle gs-0 gy-4">
                        <thead>
                            <tr className="fw-bolder text-muted">
                                <th>{t('Nome')}</th>
                                <th>{t('Prefixo')}</th>
                                <th>{t('Domínios')}</th>
                                <th>{t('Válida até')}</th>
                                <th>{t('Situação')}</th>
                                <th>{t('Último uso')}</th>
                                <th>{t('Criada em')}</th>
                                <th className="text-end">{t('Ações')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {chaves.map((chave) => (
                                <tr key={chave.id}>
                                    <td className="fw-bold">{chave.nome}</td>
                                    <td><code>{chave.prefixo}…</code></td>
                                    <td>{chave.dominios.map((dominio) => <div key={dominio}>{dominio}</div>)}</td>
                                    <td>{formatarData(chave.expiraEm)}</td>
                                    <td><Situacao chave={chave} /></td>
                                    <td>{chave.ultimoUsoEm ? formatarDataHora(chave.ultimoUsoEm) : t('Nunca usada')}</td>
                                    <td>{formatarData(chave.criadoEm)}</td>
                                    <td className="text-end">
                                        {chave.situacao !== 'revogada' &&
                                            <button type="button" className="btn btn-sm btn-light-danger" onClick={() => revogar(chave)}>
                                                {t('Revogar')}
                                            </button>}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>}
        </>}
    </>;
}

export default ApiKeys;
